import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Fish, Ban, Camera, MapPin, Send, Sparkles, Waves, Thermometer, Wind, Loader2 } from "lucide-react";
import { submitFishingReport, fileUrl } from "@/lib/api";
import { CATCH_STYLE } from "@/components/FishingMap";

const CATCH_TYPES = [
  { id: "good_fish", label: "Good fish", icon: Fish },
  { id: "jellyfish", label: "Jellyfish", icon: Ban },
  { id: "sargassum", label: "Sargassum", icon: Waves },
  { id: "empty", label: "Empty net", icon: Ban },
];

function ago(iso) {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function InputChip({ ok, icon: Icon, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono-tel text-[9px] font-bold ${
        ok ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-600/20 text-slate-500"
      }`}
    >
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export default function FishingPanel({ advisory, placed, onPlaced, onRefetch, species, onSpecies }) {
  const [catchType, setCatchType] = useState("good_fish");
  const [catchSpecies, setCatchSpecies] = useState("");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const inputs = advisory?.inputs || {};
  const reports = advisory?.reports || [];
  const good = advisory?.good_zones || [];
  const avoid = advisory?.avoid_zones || [];

  const src = advisory?.data_sources || {};
  const sstDate = src.sst?.obs_time ? String(src.sst.obs_time).slice(0, 10) : null;

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!placed) {
      toast.error("Tap the map to set your catch location first");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("lat", placed.lat);
      fd.append("lng", placed.lng);
      fd.append("catch_type", catchType);
      fd.append("species", catchSpecies);
      fd.append("note", note);
      if (photo) fd.append("photo", photo);
      const res = await submitFishingReport(fd);
      const ai = res.ai_label?.label;
      toast.success(ai ? `Report logged · AI sees: ${ai.replace("_", " ")}` : "Catch report logged. Thank you!");
      setCatchSpecies(""); setNote(""); setPhoto(null); setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      onPlaced?.(null);
      onRefetch?.();
    } catch (e) {
      toast.error("Could not submit report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass flex h-full flex-col overflow-hidden rounded-xl" data-testid="fishing-panel">
      <div className="shrink-0 border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <Fish className="h-4 w-4 text-cyanx" />
          <div className="font-display text-sm font-extrabold tracking-tight text-white">CATCH INTELLIGENCE</div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          <InputChip ok={inputs.currents} icon={Waves} label="CURRENTS" />
          <InputChip ok={inputs.sst} icon={Thermometer} label={sstDate ? `SST ${sstDate}` : "SST"} />
          <InputChip ok={inputs.sargassum} icon={Sparkles} label="SARGASSUM" />
          <InputChip ok={inputs.wind} icon={Wind} label="WIND" />
        </div>
        <div className="mt-2">
          <label className="eyebrow mb-1 block text-slate-500">Target species — re-rank zones for your fishery</label>
          <select
            data-testid="species-select"
            value={species || ""}
            onChange={(e) => onSpecies?.(e.target.value || null)}
            className="w-full rounded-md border border-white/10 bg-black/50 px-2.5 py-1.5 text-[12px] font-semibold text-slate-100 focus:border-cyan-500/40 focus:outline-none"
          >
            <option value="">General (all fish)</option>
            {(advisory?.species_options || []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          {advisory?.species_name && (
            <div className="mt-1 text-[10.5px] leading-snug text-cyan-300/80">
              {(advisory.species_options || []).find((o) => o.id === advisory.species)?.note}
            </div>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
        {/* recommended zones */}
        <section>
          <div className="eyebrow mb-1.5 flex items-center gap-1.5 text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" /> Best fishing zones now
          </div>
          {good.length ? (
            <div className="space-y-1.5" data-testid="good-zones">
              {good.slice(0, 4).map((z, i) => (
                <div key={i} className="rounded-md border border-emerald-500/20 bg-emerald-500/[0.05] px-2.5 py-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-tel text-xs font-bold text-emerald-200">
                      {z.lat.toFixed(1)}°, {Math.abs(z.lng).toFixed(1)}°W
                    </span>
                    <span className="font-mono-tel text-[10px] text-emerald-300/80">
                      opp {Math.round(z.opportunity * 100)}%
                    </span>
                  </div>
                  <div className="text-[11px] leading-snug text-slate-400">{z.reason}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500">No standout zones right now — check back after the next data cycle.</div>
          )}
        </section>

        {/* avoid zones */}
        {avoid.length > 0 && (
          <section>
            <div className="eyebrow mb-1.5 flex items-center gap-1.5 text-red-300">
              <Ban className="h-3.5 w-3.5" /> Avoid — wasted fuel / bycatch
            </div>
            <div className="space-y-1.5" data-testid="avoid-zones">
              {avoid.slice(0, 3).map((z, i) => (
                <div key={i} className="rounded-md border border-red-500/20 bg-red-500/[0.05] px-2.5 py-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-tel text-xs font-bold text-red-200">
                      {z.lat.toFixed(1)}°, {Math.abs(z.lng).toFixed(1)}°W
                    </span>
                    <span className="font-mono-tel text-[10px] text-red-300/80">risk {Math.round(z.risk * 100)}%</span>
                  </div>
                  <div className="text-[11px] leading-snug text-slate-400">{z.reason}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* report form */}
        <section className="rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04] p-3">
          <div className="eyebrow mb-2 flex items-center gap-1.5 text-cyan-300">
            <Camera className="h-3.5 w-3.5" /> Report your catch
          </div>
          <div className="mb-2 flex items-center gap-1.5 text-[11px]">
            <MapPin className="h-3.5 w-3.5 text-cyanx" />
            {placed ? (
              <span className="font-mono-tel text-slate-200">
                {placed.lat.toFixed(2)}°, {Math.abs(placed.lng).toFixed(2)}°W
              </span>
            ) : (
              <span className="text-slate-500">Tap the map to set location</span>
            )}
          </div>

          <div className="mb-2 grid grid-cols-2 gap-1.5">
            {CATCH_TYPES.map((c) => (
              <button
                key={c.id}
                data-testid={`catch-type-${c.id}`}
                onClick={() => setCatchType(c.id)}
                className={`inline-flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                  catchType === c.id
                    ? "border-cyan-400/50 bg-cyan-500/20 text-cyan-100"
                    : "border-white/10 text-slate-400 hover:text-slate-200"
                }`}
              >
                <c.icon className="h-3.5 w-3.5" /> {c.label}
              </button>
            ))}
          </div>

          <input
            data-testid="species-input"
            value={catchSpecies}
            onChange={(e) => setCatchSpecies(e.target.value)}
            placeholder="Species (optional) e.g. mahi, snapper"
            className="mb-1.5 w-full rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5 text-[12px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/40 focus:outline-none"
          />
          <textarea
            data-testid="note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            rows={2}
            className="mb-1.5 w-full resize-none rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5 text-[12px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/40 focus:outline-none"
          />

          <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="photo-input" />
          <button
            onClick={() => fileRef.current?.click()}
            className="mb-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-white/15 px-2 py-2 text-[11px] text-slate-400 hover:border-cyan-500/40 hover:text-slate-200"
          >
            <Camera className="h-3.5 w-3.5" /> {photo ? "Change photo" : "Add catch photo (optional)"}
          </button>
          {preview && (
            <img src={preview} alt="preview" className="mb-2 h-24 w-full rounded-md object-cover" />
          )}

          <button
            onClick={submit}
            disabled={submitting}
            data-testid="submit-report-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-cyan-500/90 px-3 py-2 text-[12px] font-bold text-slate-950 transition-colors hover:bg-cyan-400 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {submitting ? "Submitting…" : "Submit report"}
          </button>
        </section>

        {/* recent reports */}
        <section>
          <div className="eyebrow mb-1.5 text-slate-400">Recent fisher reports ({reports.length})</div>
          {reports.length ? (
            <div className="space-y-1.5" data-testid="recent-reports">
              {reports.slice(0, 12).map((r) => {
                const st = CATCH_STYLE[r.catch_type] || CATCH_STYLE.other;
                return (
                  <div key={r.id} className="flex items-center gap-2 rounded-md border border-white/5 bg-black/30 p-1.5">
                    {r.photo_path ? (
                      <img src={fileUrl(r.photo_path)} alt="catch" className="h-10 w-10 shrink-0 rounded object-cover" />
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-white/5">
                        <Fish className="h-4 w-4 text-slate-600" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="rounded px-1 py-0.5 font-mono-tel text-[9px] font-bold" style={{ background: `${st.c}22`, color: st.c }}>
                          {st.label}
                        </span>
                        {r.species && <span className="truncate text-[11px] text-slate-300">{r.species}</span>}
                      </div>
                      <div className="truncate text-[10px] text-slate-500">
                        {r.lat.toFixed(1)}°,{Math.abs(r.lng).toFixed(1)}°W · {ago(r.created_at)}
                        {r.ai_label?.label && <span className="text-cyan-400/70"> · AI: {r.ai_label.label.replace("_", " ")}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500">Be the first to report a catch — every report sharpens the map for the whole fleet.</div>
          )}
        </section>
      </div>
    </div>
  );
}
